import { ArrowLeftOutlined, EyeOutlined } from '@ant-design/icons';
import type { Order } from '@shop-monorepo/types';
import type { TableProps } from 'antd';
import { Button, Spin, Tag, Typography } from 'antd';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { BottomPagination } from '../../components/BottomPagination';
import { OrderMobileCard } from '../../components/OrderMobileCard';
import { OrderStatusTag } from '../../components/OrderStatusTag';
import {
  FormCard,
  FormPageContainer,
  FormPageHeader,
  StyledTable,
} from '../../components/PageLayout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useListReturn } from '../../hooks/useListReturn';
import { usePagination } from '../../hooks/usePagination';
import { useUser, useUserOrders } from '../../hooks/useUserQueries';
import { ROUTES } from '../../routes/routes';
import { formatPrice } from '../../utils/currency';

const { Text } = Typography;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  > * {
    min-width: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px;
`;

const InfoLabel = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 18px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px solid #edf2f7;
  margin-top: 4px;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 6px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DELIVERY_LABELS: Record<string, string> = {
  nova_poshta: 'Нова Пошта',
  ukrposhta: 'Укрпошта',
};

export const UserViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { goToList } = useListReturn(ROUTES.USERS);
  const userId = Number(id);

  const { page, pageSize } = usePagination();

  const isMobile = useIsMobile();
  const { data: user, isLoading, error } = useUser(userId);
  const { data: paginatedOrders, isLoading: isLoadingOrders } = useUserOrders(userId, {
    page,
    limit: pageSize,
  });

  const orders = paginatedOrders?.hits || [];
  const totalOrders = paginatedOrders?.total || 0;

  const handleBack = () => goToList();
  const handleViewOrder = (orderId: number) =>
    navigate(ROUTES.ORDER_VIEW.replace(':id', String(orderId)));

  if (isLoading) {
    return (
      <FormPageContainer>
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      </FormPageContainer>
    );
  }

  if (error || !user) {
    return (
      <FormPageContainer>
        <FormPageHeader>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <h2>Користувач</h2>
        </FormPageHeader>
        <Text type="danger">{error?.message || 'Користувача не знайдено'}</Text>
      </FormPageContainer>
    );
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  const formattedDate = new Date(user.createdAt).toLocaleDateString('uk-UA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const orderColumns: TableProps<Order>['columns'] = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (val: number) => String(val).padStart(4, '0'),
    },
    {
      title: 'Сума',
      dataIndex: 'total',
      key: 'total',
      render: (val: number) => formatPrice(val),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: Order['status']) => <OrderStatusTag status={status} />,
    },
    {
      title: 'Оплата',
      key: 'isPaid',
      render: (_, record) => (
        <Tag color={record.isPaid ? 'green' : 'default'}>
          {record.isPaid ? 'Оплачено' : 'Не оплачено'}
        </Tag>
      ),
    },
    {
      title: 'Доставка',
      key: 'delivery',
      render: (_, record) => DELIVERY_LABELS[record.delivery.provider] ?? record.delivery.provider,
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) =>
        new Date(date).toLocaleDateString('uk-UA', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Button icon={<EyeOutlined />} size="small" onClick={() => handleViewOrder(record.id)} />
      ),
    },
  ];

  return (
    <FormPageContainer>
      <FormPageHeader>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
        <TitleRow>
          <h2 style={{ margin: 0 }}>{fullName}</h2>
          <Tag
            color={user.role === 'admin' ? 'red' : 'blue'}
            style={{
              borderRadius: 20,
              fontWeight: 700,
              fontSize: 11,
              textTransform: 'uppercase',
            }}
          >
            {user.role === 'admin' ? 'Адмін' : 'Користувач'}
          </Tag>
        </TitleRow>
      </FormPageHeader>

      <TwoColumnGrid>
        <div>
          <FormCard>
            <SectionTitle>Профіль</SectionTitle>

            <InfoLabel>Email</InfoLabel>
            <InfoValue>{user.email}</InfoValue>

            {user.phone && (
              <>
                <InfoLabel>Телефон</InfoLabel>
                <InfoValue>{user.phone}</InfoValue>
              </>
            )}

            <InfoLabel>Спосіб входу</InfoLabel>
            <InfoValue>
              {user.hasPassword ? <Tag>Пароль</Tag> : <Tag color="orange">Google</Tag>}
            </InfoValue>

            <InfoLabel>Розсилка</InfoLabel>
            <InfoValue>
              {user.marketingOptIn ? <Tag color="green">Так</Tag> : <Tag>Ні</Tag>}
            </InfoValue>

            <StatGrid>
              <div>
                <InfoLabel>Замовлень</InfoLabel>
                <StatValue>{user.orderCount}</StatValue>
              </div>
              <div>
                <InfoLabel>Дата реєстрації</InfoLabel>
                <StatValue style={{ fontSize: 13, fontWeight: 600, marginTop: 10 }}>
                  {formattedDate}
                </StatValue>
              </div>
            </StatGrid>
          </FormCard>
        </div>

        <div>
          <FormCard>
            <SectionTitle>Замовлення</SectionTitle>
            {isMobile ? (
              <>
                {orders.map((order) => (
                  <OrderMobileCard key={order.id} order={order} onView={handleViewOrder} />
                ))}
              </>
            ) : (
              <StyledTable
                columns={orderColumns}
                dataSource={orders}
                rowKey="id"
                loading={isLoadingOrders}
                pagination={false}
                scroll={{ x: 600 }}
              />
            )}
            <BottomPagination total={totalOrders} />
          </FormCard>
        </div>
      </TwoColumnGrid>
    </FormPageContainer>
  );
};
